// Utility functions for handling API responses

import { StandardAPIResponse } from '../types/monitoring';

/**
 * Safely extracts data from a StandardAPIResponse
 * @param response - The API response
 * @returns The data if available, otherwise null
 */
export function extractApiData<T>(response: StandardAPIResponse<T> | any): T | null {
  if (response && typeof response === 'object') {
    if ('data' in response && response.data !== undefined) {
      return response.data;
    }
    // If response doesn't have 'data' property, assume the response itself is the data
    return response as T;
  }
  return null;
}

/**
 * Checks if an API response indicates success
 * @param response - The API response
 * @returns True if the response indicates success
 */
export function isApiResponseSuccess(response: any): boolean {
  if (response && typeof response === 'object') {
    return response.success === true || response.message !== undefined;
  }
  return false;
}

/**
 * Safely handles API responses with proper type checking
 * @param response - The API response
 * @param fallback - Fallback value if data extraction fails
 * @returns The extracted data or fallback
 */
export function safeApiData<T>(response: StandardAPIResponse<T> | any, fallback: T): T {
  const data = extractApiData<T>(response);
  return data !== null ? data : fallback;
}
